import { getLevelLabel } from '@app/src/pages/Assessments/Assessment/utils'
import type { AssessmentLevelNamesType } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { getAssessmentLevelConfig, resolveLevelType } from '@app/src/utils/assessmentLevels'
import { LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import { Box, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface LevelProps {
  value?: number
  levelsCount?: number
  percentage?: number
  assessmentType?: AssessmentType
  levelNamesType?: AssessmentLevelNamesType
  thresholds: number[]
  reducedHeight?: boolean
}

const LevelBar: React.FC<LevelProps> = ({
  value,
  levelsCount = 5,
  percentage,
  assessmentType,
  levelNamesType,
  thresholds,
  reducedHeight = false,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const getDisplayLabel = () => {
    if (percentage !== undefined) {
      return `${percentage}${formatMessage({ id: 'companyProfile.percentage' })}`
    }

    if (assessmentType === AssessmentType.BaselineAssessment) {
      return formatMessage({ id: 'baselineAssessment.totalLevel' }, { value })
    }

    if (value && assessmentType && levelNamesType) {
      return getLevelLabel(value, levelsCount, assessmentType, levelNamesType, formatMessage, LabelFormat.Basic)
    }

    return formatMessage({ id: 'general.notAvailable' })
  }

  const levelType = resolveLevelType(levelsCount, thresholds, value, percentage)
  const levelConfig = getAssessmentLevelConfig(theme, formatMessage)[levelType]

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      borderRadius="100px"
      bgcolor={levelConfig.bgColor}
      padding="2px 12px 2px 10px"
      height={reducedHeight ? 26 : 32}
    >
      <Box width={8} height={8} borderRadius="50%" bgcolor={levelConfig.dotColor} marginRight="8px" />
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontSize: '1.75rem',
          lineHeight: '2.5rem',
          letterSpacing: '0.1px',
          color: levelConfig.textColor || theme.palette.text.primary,
        }}
      >
        {getDisplayLabel()}
      </Typography>
    </Box>
  )
}

export default LevelBar
